import { IDebtor } from '@/pages/main/types';

import { FC, useState } from 'react';

import { useForm } from 'react-hook-form';

import axios from 'axios';

import { Input } from '../inputs';
import { Button } from '../button';
import Modal from '../modal';
import SuccessModal from './SuccessModal';

import { useAppDispatch } from '@/store/store';
import { updateClaimStatus } from '@/store/slices/claimsSlice';

import { updateDebtor } from '@/app/api';

import AlertLogo from '@/assets/modal/alert.gif';

interface IFormModal {
  isOpen: boolean;
  setIsOpen: (status: boolean) => void;
  id: number;
  debtor: IDebtor;
}

export interface IFormData {
  email: string;
  phoneNumber: string;
  address: string;
  postCode: string;
  city: string;
}

const FormModal: FC<IFormModal> = ({ isOpen, setIsOpen, id, debtor }) => {
  const [isOpenSuccess, setIsOpenSuccess] = useState(false);

  const dispatch = useAppDispatch();

  const handleUpdateInformation = () => {
    dispatch(updateClaimStatus(id));
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<IFormData>({
    defaultValues: {
      email: debtor?.is_imported_email ? debtor?.email : '',
      phoneNumber: debtor?.is_imported_phone ? debtor?.phone : '',
      address: debtor?.is_imported_address_1 ? debtor?.address_1 : '',
      postCode: debtor?.is_imported_postcode ? debtor?.postcode : '',
      city: debtor?.is_imported_city ? debtor?.city : '',
    },
  });

  const onClose = () => {
    setIsOpen(false);
  };

  const onSubmit = (data: IFormData) => {
    updateDebtor(id, data)
      .then(() => {
        onClose();

        setIsOpenSuccess(true);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) && error?.response) {
          const { response } = error;

          if (response.status === 422 && response.data?.errors) {
            Object.entries(response.data.errors).forEach(
              ([field, messages]) => {
                setError(field as keyof IFormData, {
                  type: 'server',
                  message: Array.isArray(messages) ? messages[0] : messages,
                });
              }
            );
          }
        }
      });
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose} className={'bg-white'}>
        <div className="bg-red-600 text-white text-center py-3 rounded-t-lg">
          <div className="flex flex-col items-center">
            <img src={AlertLogo} alt="alert" className="w-[40px] h-[40px]" />
            <p className="text-[28px] font-jostMedium">Manglende information</p>
          </div>
        </div>
        <form
          className="p-7 space-y-6 flex flex-col items-center"
          onSubmit={handleSubmit(onSubmit)}>
          <p className="text-center text-[22px] text-[#1F2B40] font-jostRegular">
            Udfyld venligst de manglende oplysninger for at komme videre med
            inkasso
          </p>
          <Input
            placeholder="Navn"
            className="!text-[#1F2B40CC] !font-jostRegular placeholder:text-[#1F2B40CC]/80"
            value={debtor?.name}
            readOnly
          />
          <Input
            placeholder="E-mail"
            type="email"
            className="!text-[#1F2B40CC] !font-jostRegular placeholder:text-[#1F2B40CC]/80"
            {...register('email', { required: `Påkrævet felt E-Mail` })}
            error={errors?.email?.message}
            readOnly={!!debtor?.is_imported_email}
          />
          <Input
            placeholder="Telefon"
            type="tel"
            className="!text-[#1F2B40CC] !font-jostRegular placeholder:text-[#1F2B40CC]/80"
            {...register('phoneNumber', { required: `Påkrævet felt Telefon` })}
            error={errors?.phoneNumber?.message}
            readOnly={!!debtor?.is_imported_phone}
          />
          <Input
            placeholder="Adresse"
            className="!text-[#1F2B40CC] !font-jostRegular placeholder:text-[#1F2B40CC]/80"
            {...register('address', { required: `Påkrævet felt Adresse` })}
            error={errors?.address?.message}
            readOnly={!!debtor?.is_imported_address_1}
          />
          <div className="flex space-x-3 w-full">
            <div className="flex-1/3">
              <Input
                placeholder="Postnummer"
                type="number"
                className="!text-[#1F2B40CC] !font-jostRegular placeholder:text-[#1F2B40CC]/80 w-full"
                {...register('postCode', {
                  required: `Påkrævet felt Postnummer`,
                })}
                error={errors?.postCode?.message}
                readOnly={!!debtor?.is_imported_postcode}
              />
            </div>
            <div className="flex-2/3">
              <Input
                placeholder="By"
                className="!text-[#1F2B40CC] !font-jostRegular placeholder:text-[#1F2B40CC]/80 w-full"
                {...register('city', { required: `Påkrævet felt By` })}
                error={errors?.city?.message}
                readOnly={!!debtor?.is_imported_city}
              />
            </div>
          </div>
          <Button
            className="w-[201px] h-[40px] bg-[#1F2B40] !font-jostRegular"
            type="submit">
            Gem
          </Button>
        </form>
      </Modal>

      <SuccessModal
        isOpen={isOpenSuccess}
        setIsOpen={setIsOpenSuccess}
        title="Opdateret med succes!"
        action={handleUpdateInformation}
      />
    </>
  );
};

export default FormModal;
